import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CalendarDays, Clock, CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import { pl } from 'date-fns/locale';
import { api } from '@/lib/api';
import { KRAFT } from '@/constants/testIds';

const SLOTS = [
  '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00', '16:00', '17:00',
];

const SERVICES = [
  'Awaria instalacji',
  'Modernizacja rozdzielni',
  'Pomiary i protokoły odbiorowe',
  'Instalacja domowa / mieszkanie',
  'Instalacja komercyjna / biuro',
  'Wymiana zabezpieczeń / RCD',
  'Oświetlenie i osprzęt',
  'Konsultacja techniczna',
];

const formatDate = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatHuman = (d) => {
  if (!d) return '—';
  return d.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

export default function Booking() {
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [takenSlots, setTakenSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    address: '',
    notes: '',
  });

  // For Sundays and past dates disabled
  const disabledMatcher = useMemo(
    () => (day) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (day < today) return true;
      if (day.getDay() === 0) return true; // Nd zamknięte
      return false;
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    async function loadSlots() {
      if (!date) return;
      setLoadingSlots(true);
      try {
        const res = await api.get('/bookings/taken-slots', { params: { date: formatDate(date) } });
        if (!cancelled) setTakenSlots(res.data?.taken || []);
      } catch (e) {
        if (!cancelled) setTakenSlots([]);
      } finally {
        if (!cancelled) setLoadingSlots(false);
      }
    }
    loadSlots();
    return () => { cancelled = true; };
  }, [date]);

  // Filter slots that fit business hours (Sat until 14:00)
  const availableSlots = useMemo(() => {
    if (!date) return [];
    if (date.getDay() === 6) return SLOTS.filter((s) => s < '14:00');
    return SLOTS;
  }, [date]);

  const canSubmit = date && slot && form.name.trim() && form.email.trim() && form.phone.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        date: formatDate(date),
        time_slot: slot,
        service: form.service || null,
        address: form.address.trim() || null,
        notes: form.notes.trim() || null,
      };
      const res = await api.post('/bookings', payload);
      setConfirmed(res.data);
      toast.success('Rezerwacja przyjęta', {
        description: `Potwierdzenie wysłane na ${form.email}.`,
      });
      // reset form (keep confirmed for display)
      setForm({ name: '', email: '', phone: '', service: '', address: '', notes: '' });
      setSlot(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Nie udało się zapisać rezerwacji. Spróbuj ponownie.';
      toast.error('Błąd rezerwacji', { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid={KRAFT.bookingRoot}
      className="relative bg-ink-900 py-24 md:py-32 border-y border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-px bg-volt" />
          <span className="eyebrow">Rezerwacja standardowa</span>
        </div>
        <h2 className="font-display uppercase text-white text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight max-w-4xl">
          Zarezerwuj termin <span className="text-volt italic font-normal">bez telefonów.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-slate-300 leading-relaxed">
          Wybierz dzień i godzinę — potwierdzimy zgłoszenie e-mailem. W nagłych sytuacjach nie rezerwuj — 
          zadzwoń natychmiast na <a className="text-volt underline underline-offset-4" href="tel:+48731842736">+48 731 842 736</a>.
        </p>

        <AnimatePresence mode="wait">
          {confirmed ? (
            <motion.div
              key="confirmed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-16 relative border border-volt/70 bg-ink-950 p-10 md:p-14"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 border border-volt bg-volt/10 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-volt" strokeWidth={2} />
                </div>
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-volt">Rezerwacja przyjęta</div>
                  <div className="font-display uppercase text-white text-2xl md:text-3xl tracking-tight mt-1">
                    {confirmed.date} · {confirmed.time_slot}
                  </div>
                </div>
              </div>
              <p className="text-slate-300 max-w-xl">
                Wysłaliśmy potwierdzenie na <span className="text-white">{confirmed.email}</span>. Skontaktujemy 
                się telefonicznie w celu ostatecznego doprecyzowania zakresu prac.
              </p>
              <button
                onClick={() => setConfirmed(null)}
                className="mt-8 btn-outline"
              >
                Zarezerwuj kolejny termin
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/10"
            >
              {/* Calendar + slots */}
              <div className="lg:col-span-5 bg-ink-900 p-8 md:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <CalendarDays className="w-5 h-5 text-volt" />
                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-300">1. Wybierz datę</div>
                </div>

                <div data-testid={KRAFT.bookingCalendar} className="kraft-calendar">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => { setDate(d); setSlot(null); }}
                    disabled={disabledMatcher}
                    locale={pl}
                    weekStartsOn={1}
                    className="!bg-transparent"
                  />
                </div>

                <div className="mt-8 pt-8 border-t border-white/10">
                  <div className="flex items-center gap-3 mb-6">
                    <Clock className="w-5 h-5 text-volt" />
                    <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-300">
                      2. Wybierz godzinę
                    </div>
                    {loadingSlots && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
                  </div>
                  {!date ? (
                    <p className="text-slate-400 text-sm">Najpierw wybierz datę.</p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {availableSlots.map((s) => {
                        const taken = takenSlots.includes(s);
                        const active = slot === s;
                        return (
                          <button
                            key={s}
                            data-testid={KRAFT.bookingSlot(s)}
                            type="button"
                            disabled={taken}
                            onClick={() => setSlot(s)}
                            className={`relative py-2.5 px-2 font-mono text-sm tracking-wider border transition-colors ${
                              active
                                ? 'bg-volt text-ink-950 border-volt'
                                : taken
                                ? 'bg-ink-950 border-white/5 text-slate-600 line-through cursor-not-allowed'
                                : 'bg-ink-950 border-white/10 text-white hover:border-volt/60 hover:text-volt'
                            }`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Form */}
              <form
                onSubmit={handleSubmit}
                className="lg:col-span-7 bg-ink-900 p-8 md:p-10 space-y-6"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-300">3. Twoje dane</div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Imię i nazwisko *</Label>
                    <Input
                      data-testid={KRAFT.bookingInputName}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      className="mt-2 bg-ink-950 border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-volt text-white h-12"
                      placeholder="np. Jan Kowalski"
                    />
                  </div>
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Telefon *</Label>
                    <Input
                      data-testid={KRAFT.bookingInputPhone}
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      required
                      type="tel"
                      className="mt-2 bg-ink-950 border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-volt text-white h-12"
                      placeholder="+48 ..."
                    />
                  </div>
                </div>

                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">E-mail *</Label>
                  <Input
                    data-testid={KRAFT.bookingInputEmail}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    type="email"
                    className="mt-2 bg-ink-950 border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-volt text-white h-12"
                    placeholder="ty@firma.pl"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Zakres prac</Label>
                    <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                      <SelectTrigger
                        data-testid={KRAFT.bookingInputService}
                        className="mt-2 bg-ink-950 border-white/10 rounded-none focus:ring-0 text-white h-12"
                      >
                        <SelectValue placeholder="Wybierz z listy" />
                      </SelectTrigger>
                      <SelectContent className="bg-ink-950 border-white/10 rounded-none text-white">
                        {SERVICES.map((s) => (
                          <SelectItem key={s} value={s} className="focus:bg-volt/10 focus:text-volt">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Adres wizyty</Label>
                    <Input
                      data-testid={KRAFT.bookingInputAddress}
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      className="mt-2 bg-ink-950 border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-volt text-white h-12"
                      placeholder="ul. ..., Warszawa"
                    />
                  </div>
                </div>

                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Opis / uwagi</Label>
                  <Textarea
                    data-testid={KRAFT.bookingInputNotes}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={4}
                    className="mt-2 bg-ink-950 border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-volt text-white resize-none"
                    placeholder="Krótko opisz problem lub oczekiwany zakres prac..."
                  />
                </div>

                {/* Summary strip */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6 border-t border-white/10">
                  <div className="border border-white/10 p-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Wybrana data</div>
                    <div className="font-display uppercase text-white text-lg tracking-tight mt-1">
                      {formatHuman(date)}
                    </div>
                  </div>
                  <div className="border border-white/10 p-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Godzina</div>
                    <div className="font-display uppercase text-white text-lg tracking-tight mt-1">
                      {slot || '—'}
                    </div>
                  </div>
                </div>

                <button
                  data-testid={KRAFT.bookingSubmit}
                  type="submit"
                  disabled={!canSubmit || submitting}
                  className="btn-volt w-full md:w-auto disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:bg-volt disabled:hover:text-ink-950"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Wysyłanie...
                    </>
                  ) : (
                    <>
                      Zarezerwuj termin
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
